<!-- EchartsHalfPieChart.vue -->
<!-- 半圆环图 -->
<template>
  <div class="half-pie-chart-container">
    <div ref="chart" :style="{ width: width, height: height }"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'HalfPieChart',
  props: {
    // 尺寸配置
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '400px'
    },
    
    // 数据配置
    chartData: {
      type: Array,
      default: () => [
        { value: 1048, name: '搜索引擎' },
        { value: 735, name: '直接访问' },
        { value: 580, name: '邮件营销' },
        { value: 484, name: '联盟广告' },
        { value: 300, name: '视频广告' }
      ]
    },
    
    // 图表标题
    title: {
      type: String,
      default: ''
    },
    
    // 系列名称
    seriesName: {
      type: String,
      default: '访问来源'
    },
    
    // 样式配置
    radius: {
      type: Array,
      default: () => ['40%', '70%']
    },
    
    // 中心位置
    center: {
      type: Array,
      default: () => ['50%', '70%']
    },
    
    // 起始角度和结束角度
    startAngle: {
      type: Number,
      default: 180
    },
    endAngle: {
      type: Number,
      default: 360
    },
    
    // 图例配置
    showLegend: {
      type: Boolean,
      default: true
    },
    legendPosition: {
      type: String,
      default: 'top-center',
      validator: (value) => ['top-center', 'top-left', 'top-right', 'bottom', 'left', 'right', 'none'].includes(value)
    },
    // 新增：颜色序列配置
    colors: {
      type: Array,
      default: () => [
        '#5470c6', '#91cc75', '#fac858', '#ee6666', 
        '#73c0de', '#3ba272', '#fc8452', '#9a60b4',
        '#ea7ccc', '#7ebf42', '#e6d450', '#f2a7c3',
        '#b6a2de', '#5ab1ef', '#ffb980', '#d87a80'
      ]
    },
    // 是否显示标签
    showLabel: {
      type: Boolean,
      default: true
    },
    
    // 标签位置
    labelPosition: {
      type: String,
      default: 'inside',
      validator: (value) => ['inside', 'outside', 'center'].includes(value)
    },

    // 新增下载相关属性
    downloadFileName: {
      type: String,
      default: 'half_pie_chart'
    },
    downloadType: {
      type: String,
      default: 'png',
      validator: (value) => ['png', 'jpeg'].includes(value)
    },
    // 新增：是否显示工具箱
    showToolbox: {
      type: Boolean,
      default: true
    },
    // 新增：下载时使用的背景色
    downloadBackgroundColor: {
      type: String,
      default: '#ffffff'
    }
  },
  
  data() {
    return {
      chart: null,
      resizeObserver: null,
      defaultColors: [
        '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
        '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'
      ],
      internalChartData: []
    };
  },
  
  mounted() {
    this.internalChartData = [...this.chartData];
    this.initChart();
    this.setupResizeObserver();
  },
  
  beforeDestroy() {
    this.disposeChart();
    this.removeResizeObserver();
  },
  
  computed: {
    finalColors() {
      return this.colors.length > 0 ? this.colors : this.defaultColors;
    }
  },
  
  methods: {
    // 下载图片
    downloadImage() {
      if (!this.chart) {
        console.warn('图表实例未初始化');
        return false;
      }

      try {
        // 获取图表图片的 Data URL
        const dataURL = this.chart.getDataURL({
          type: this.downloadType,
          pixelRatio: 2,
          backgroundColor: this.downloadBackgroundColor,
          excludeComponents: ['toolbox']
        });

        // 创建下载链接
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `${this.downloadFileName}_${new Date().getTime()}.${this.downloadType}`;
        
        // 触发下载
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('半饼图下载成功');
        return true;
      } catch (error) {
        console.error('半饼图下载失败:', error);
        alert('下载失败，请重试');
        return false;
      }
    },

    // 获取工具箱配置
    getToolboxConfig() {
      if (!this.showToolbox) {
        return undefined;
      }
      
      return {
        feature: {
          saveAsImage: {
            title: '保存为图片',
            type: this.downloadType,
            name: this.downloadFileName,
            pixelRatio: 2,
            backgroundColor: this.downloadBackgroundColor,
            excludeComponents: ['toolbox'],
            // icon: 'path://M8.2 0.8C7 0.8 6 1.8 6 3v16c0 1.2 1 2.2 2.2 2.2h11.6c1.2 0 2.2-1 2.2-2.2V7.4L14.6 0.8H8.2z',
            // emphasis: {
            //   iconStyle: {
            //     color: '#1890ff'
            //   }
            // }
          }
        },
        right: 10,
        top: 10,
        iconStyle: {
          borderColor: '#666'
        }
      };
    },
    
    initChart() {
      if (!this.$refs.chart) {
        console.warn('图表容器未找到');
        return;
      }
      
      try {
        this.chart = echarts.init(this.$refs.chart);
        this.updateChart();
        
        window.addEventListener('resize', this.handleResize);
      } catch (error) {
        console.error('初始化图表失败:', error);
      }
    },
    
    disposeChart() {
      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
      window.removeEventListener('resize', this.handleResize);
    },
    
    setupResizeObserver() {
      if (typeof ResizeObserver !== 'undefined' && this.$refs.chart) {
        this.resizeObserver = new ResizeObserver(() => {
          this.handleResize();
        });
        this.resizeObserver.observe(this.$refs.chart);
      }
    },
    
    removeResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    
    // 获取图例配置
    getLegendConfig() {
      if (!this.showLegend || this.legendPosition === 'none') {
        return undefined;
      }
      
      const legendData = this.internalChartData.map(item => item.name);
      
      switch (this.legendPosition) {
        case 'top-center':
          return {
            data: legendData,
            top: '5%',
            left: 'center',
            orient: 'horizontal'
          };
        case 'top-left':
          return {
            data: legendData,
            top: '5%',
            left: 'left',
            orient: 'horizontal'
          };
        case 'top-right':
          return {
            data: legendData,
            top: '5%',
            right: 'right',
            orient: 'horizontal'
          };
        case 'bottom':
          return {
            data: legendData,
            bottom: '0%',
            left: 'center',
            orient: 'horizontal'
          };
        case 'left':
          return {
            data: legendData,
            left: 'left',
            top: 'center',
            orient: 'vertical'
          };
        case 'right':
          return {
            data: legendData,
            right: 'right',
            top: 'center',
            orient: 'vertical'
          };
        default:
          return {
            data: legendData,
            top: '5%',
            left: 'center',
            orient: 'horizontal'
          };
      }
    },
    
    // 获取标签配置
    getLabelConfig() {
      if (!this.showLabel) {
        return { show: false };
      }
      
      const baseConfig = {
        show: true,
        formatter: '{b}: {c} ({d}%)'
        
      };
      
      switch (this.labelPosition) {
        case 'inside':
          return {
            ...baseConfig,
            position: 'inside'
          };
        case 'outside':
          return {
            ...baseConfig,
            position: 'outside',
            bleedMargin: 5
          };
        case 'center':
          return {
            ...baseConfig,
            position: 'center'
          };
        default:
          return baseConfig;
      }
    },
    
    updateChart() {
      if (!this.chart) {
        return;
      }
      
      try {
        const displayData = this.internalChartData.length > 0 
          ? this.internalChartData 
          : [...this.chartData];
        
        const dataWithColors = displayData.map((item, index) => ({
          ...item,
          itemStyle: {
            color: this.finalColors[index % this.finalColors.length]
          }
        }));
        
        const option = {
          tooltip: {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c} ({d}%)'
          },
          legend: this.getLegendConfig(),
          title: this.title ? {
            text: this.title,
            left: 'center',
            top: 20,
            textStyle: {
              fontSize: 16,
              fontWeight: 'normal',
              color: '#333'
            }
          } : undefined,
          // 添加工具箱配置
          toolbox: this.getToolboxConfig(),
          series: [
            {
              name: this.seriesName,
              type: 'pie',
              radius: this.radius,
              center: this.center,
              startAngle: this.startAngle,
              endAngle: this.endAngle,
              data: dataWithColors,
              label: this.getLabelConfig(),
              labelLine: {
                show: this.labelPosition === 'outside',
                length: 10,
                length2: 20
              },
              emphasis: {
                scale: true,
                scaleSize: 10,
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                },
                label: {
                  show: true,
                  fontSize: '14',
                  fontWeight: 'bold'
                }
              },
              animationType: 'scale',
              animationEasing: 'elasticOut',
              animationDelay: () => Math.random() * 200
            }
          ]
        };
        
        this.chart.setOption(option, true);
      } catch (error) {
        console.error('更新图表失败:', error);
      }
    },
    
    handleResize() {
      if (this.chart) {
        try {
          this.chart.resize();
        } catch (error) {
          console.error('调整图表大小失败:', error);
        }
      }
    },
    
    // 提供给外部调用的方法
    resize() {
      this.handleResize();
    },
    
    // 更新数据
    setData(newData) {
      this.internalChartData = [...newData];
      this.updateChart();
    },
    
    // 显示加载动画
    showLoading() {
      if (this.chart) {
        this.chart.showLoading();
      }
    },
    
    // 隐藏加载动画
    hideLoading() {
      if (this.chart) {
        this.chart.hideLoading();
      }
    },
    
    // 获取图表实例
    getChartInstance() {
      return this.chart;
    },
    
    // 获取当前数据
    getCurrentData() {
      return [...this.internalChartData];
    },
    
    // 清空数据
    clearData() {
      this.internalChartData = [];
      this.updateChart();
    },
    
    // 添加数据项
    addDataItem(item) {
      this.internalChartData.push({ ...item });
      this.updateChart();
    }
  },
  
  watch: {
    chartData: {
      handler(newData) {
        this.internalChartData = [...newData];
        this.updateChart();
      },
      deep: true
    },
    title() {
      this.updateChart();
    },
    seriesName() {
      this.updateChart();
    },
    radius() {
      this.updateChart();
    },
    center() {
      this.updateChart();
    },
    startAngle() {
      this.updateChart();
    },
    endAngle() {
      this.updateChart();
    },
    showLegend() {
      this.updateChart();
    },
    legendPosition() {
      this.updateChart();
    },
    colors() {
      this.updateChart();
    },
    showLabel() {
      this.updateChart();
    },
    labelPosition() {
      this.updateChart();
    },
    showToolbox() {
      this.updateChart();
    },
    width() {
      this.$nextTick(() => this.handleResize());
    },
    height() {
      this.$nextTick(() => this.handleResize());
    }
  },

  // 暴露方法给父组件
  expose: [
    'downloadImage', 
    'resize', 
    'setData', 
    'clearData', 
    'addDataItem', 
    'getCurrentData', 
    'getChartInstance',
    'showLoading',
    'hideLoading'
  ]
};
</script>

<style scoped>
.half-pie-chart-container {
  width: 100%;
  height: 100%;
  position: relative;
}
</style>